import type { MultiTilePaintMode } from "@west-marches/shared";
import type {
  AssetRecord,
  CollectionRecord,
  CollectionTileSetRecord,
  OverlayRecord,
  TaxonomyNodeRecord,
  TileRecord,
  TileSetEntryRecord,
  TileSetRecord,
  TileSetTaxonomyNodeRecord,
} from "@west-marches/shared";
import type {
  MoveSystemTileResult,
  SystemCatalogKnownIds,
  TileCatalogAdminData,
} from "@/app/admin/adminTypes";
import { getSystemTileSetDeletionError } from "@/app/admin/adminTypes";
import { resolveTileAssetUrl } from "@west-marches/shared";

/** Public or CDN URL for a tile row (DB `asset_url` when set, else derived from `storage_path`). */
export function getTileRecordPreviewUrl(
  tile: Pick<TileRecord, "asset_url" | "storage_path">,
): string | null {
  const direct = tile.asset_url?.trim();
  if (direct) {
    return direct;
  }
  const path = tile.storage_path?.trim();
  if (!path) {
    return null;
  }
  return resolveTileAssetUrl(path);
}

export function getOverlayRecordPreviewUrl(
  overlay: Pick<OverlayRecord, "asset_id">,
  assetById: Map<string, Pick<AssetRecord, "asset_url" | "storage_path">>,
): string | null {
  const asset = assetById.get(overlay.asset_id);
  if (!asset) {
    return null;
  }
  const direct = asset.asset_url?.trim();
  if (direct) {
    return direct;
  }
  const path = asset.storage_path?.trim();
  if (!path) {
    return null;
  }
  return resolveTileAssetUrl(path);
}

export type SystemCatalogSelection =
  | { kind: "root" }
  | { kind: "collection"; id: string }
  | { kind: "taxonomy"; id: string }
  | { kind: "tile-set"; id: string }
  | { kind: "orphans" }
  | { kind: "pending-deletions" };

const SYSTEM_CATALOG_SELECTION_PARAM = "view";
const SYSTEM_CATALOG_SELECTION_ID_PARAM = "id";

export function buildSystemCatalogSelectionSearchParams(
  selection: SystemCatalogSelection,
): URLSearchParams {
  const params = new URLSearchParams();
  if (selection.kind === "root") {
    return params;
  }

  params.set(SYSTEM_CATALOG_SELECTION_PARAM, selection.kind);
  if ("id" in selection) {
    params.set(SYSTEM_CATALOG_SELECTION_ID_PARAM, selection.id);
  }
  return params;
}

export function parseSystemCatalogSelectionFromSearchParams(
  data:
    | Pick<TileCatalogAdminData, "collections" | "taxonomyNodes" | "tileSets">
    | SystemCatalogKnownIds,
  searchParams: Pick<URLSearchParams, "get">,
): SystemCatalogSelection {
  const collectionIds =
    "collectionIds" in data ? data.collectionIds : data.collections.map((collection) => collection.id);
  const taxonomyNodeIds =
    "taxonomyNodeIds" in data ? data.taxonomyNodeIds : data.taxonomyNodes.map((node) => node.id);
  const tileSetIds = "tileSetIds" in data ? data.tileSetIds : data.tileSets.map((tileSet) => tileSet.id);
  const kind = searchParams.get(SYSTEM_CATALOG_SELECTION_PARAM);
  const id = searchParams.get(SYSTEM_CATALOG_SELECTION_ID_PARAM)?.trim() ?? "";
  if (kind === "orphans") {
    return { kind: "orphans" };
  }
  if (kind === "pending-deletions") {
    return { kind: "pending-deletions" };
  }
  if (kind === "collection" && id && collectionIds.includes(id)) {
    return { kind: "collection", id };
  }
  if (kind === "taxonomy" && id && taxonomyNodeIds.includes(id)) {
    return { kind: "taxonomy", id };
  }
  if (kind === "tile-set" && id && tileSetIds.includes(id)) {
    return { kind: "tile-set", id };
  }
  return { kind: "root" };
}

export function getTaxonomyNodePath(
  data: Pick<TileCatalogAdminData, "taxonomyNodes">,
  taxonomyNodeId: string,
): TaxonomyNodeRecord[] {
  const nodeById = new Map(data.taxonomyNodes.map((node) => [node.id, node]));
  const path: TaxonomyNodeRecord[] = [];
  const visited = new Set<string>();
  let currentId: string | null = taxonomyNodeId;

  while (currentId) {
    if (visited.has(currentId)) {
      break;
    }
    visited.add(currentId);
    const node = nodeById.get(currentId);
    if (!node) {
      break;
    }
    path.unshift(node);
    currentId = node.parent_id;
  }

  return path;
}

export function getTaxonomyTreeExpansionKeys(
  data: Pick<TileCatalogAdminData, "taxonomyNodes">,
  taxonomyNodeId: string,
): string[] {
  return getTaxonomyNodePath(data, taxonomyNodeId).map((node) => `taxonomy:${node.id}`);
}

export function getTileSetTreeExpansionKeys(
  data: Pick<TileCatalogAdminData, "taxonomyNodes" | "tileSetTaxonomyNodes">,
  tileSetId: string,
): string[] {
  const keys = new Set<string>();

  for (const membership of data.tileSetTaxonomyNodes) {
    if (membership.tile_set_id !== tileSetId) {
      continue;
    }
    if (membership.taxonomy_node_id) {
      for (const key of getTaxonomyTreeExpansionKeys(data, membership.taxonomy_node_id)) {
        keys.add(key);
      }
    }
    keys.add(`tile-set:${tileSetId}`);
  }

  return [...keys];
}

export function getTreeExpansionKeysForSelection(
  data: Pick<TileCatalogAdminData, "taxonomyNodes" | "tileSetTaxonomyNodes">,
  selection: SystemCatalogSelection,
): string[] {
  if (selection.kind === "taxonomy") {
    return getTaxonomyTreeExpansionKeys(data, selection.id);
  }
  if (selection.kind === "tile-set") {
    return getTileSetTreeExpansionKeys(data, selection.id);
  }
  return [];
}

export interface SystemCatalogDeleteImpact {
  folderCount: number;
  tileSetCount: number;
  orphanTileSetCount: number;
}

export interface PendingDeletionTile {
  tile: TileRecord;
  asset: AssetRecord | null;
  daysRemaining: number;
  purgeAfter: string;
}

export type TaxonomyMixedEntry =
  | {
      kind: "taxonomy-node";
      id: string;
      label: string;
      sortOrder: number;
      node: TaxonomyNodeRecord;
    }
  | {
      kind: "tile-set";
      id: string;
      label: string;
      sortOrder: number;
      tileSet: TileSetRecord;
    };

export type TileSetOrderEntry =
  | {
      kind: "tile";
      id: string;
      sortOrder: number;
      tile: TileRecord;
      recordId: string | null;
    }
  | {
      kind: "tile-set";
      id: string;
      sortOrder: number;
      tileSet: TileSetRecord;
      recordId: string;
    };

export interface SystemCatalogDerivedData {
  assetById: Map<string, AssetRecord>;
  overlayById: Map<string, OverlayRecord>;
  tileById: Map<string, TileRecord>;
  tileSetById: Map<string, TileSetRecord>;
  tilesByTileSetId: Map<string, TileRecord[]>;
  decorationsByTaxonomyId: Map<string | null, OverlayRecord[]>;
  tileSetOrderEntriesByTileSetId: Map<string, TileSetOrderEntry[]>;
  tileCountByTileSetId: Map<string, number>;
  taxonomyChildrenByParentId: Map<string | null, TaxonomyNodeRecord[]>;
  taxonomyMixedEntriesByParentId: Map<string | null, TaxonomyMixedEntry[]>;
  overlayPreviewUrlById: Map<string, string | null>;
  tileSetPreviewUrlById: Map<string, string | null>;
  taxonomyPreviewUrlById: Map<string, string | null>;
}

export function buildSystemCatalogDerivedData(data: TileCatalogAdminData): SystemCatalogDerivedData {
  const assets = data.assets ?? [];
  const overlays = data.overlays ?? [];
  const tiles = data.tiles ?? [];
  const tileSets = data.tileSets ?? [];
  const taxonomyNodes = data.taxonomyNodes ?? [];
  const tileSetOrderEntries = data.tileSetOrderEntries ?? [];
  const tileSetTaxonomyNodes = data.tileSetTaxonomyNodes ?? [];
  const taxonomyNodeOverlays = data.taxonomyNodeOverlays ?? [];
  const assetById = new Map(assets.map((asset) => [asset.id, asset] as const));
  const overlayById = new Map(overlays.map((overlay) => [overlay.id, overlay] as const));
  const tileById = new Map(tiles.map((tile) => [tile.id, tile] as const));
  const tileSetById = new Map(tileSets.map((tileSet) => [tileSet.id, tileSet] as const));
  const taxonomyNodeById = new Map(taxonomyNodes.map((node) => [node.id, node] as const));
  const tilePreviewUrlById = new Map(tiles.map((tile) => [tile.id, getTileRecordPreviewUrl(tile)] as const));
  const decorationsByTaxonomyId = new Map<string | null, OverlayRecord[]>();
  const tilesByTileSetId = new Map<string, TileRecord[]>();
  const tileSetOrderEntriesByTileSetId = new Map<string, TileSetOrderEntry[]>();
  const tileCountByTileSetId = new Map<string, number>();
  const taxonomyChildrenByParentId = new Map<string | null, TaxonomyNodeRecord[]>();
  const taxonomyMixedEntriesByParentId = new Map<string | null, TaxonomyMixedEntry[]>();
  const overlayPreviewUrlById = new Map<string, string | null>();
  const tileSetPreviewUrlById = new Map<string, string | null>();
  const taxonomyPreviewUrlById = new Map<string, string | null>();
  const directTileSetsByTaxonomyId = new Map<string | null, TileSetRecord[]>();

  for (const node of taxonomyNodes) {
    const siblings = taxonomyChildrenByParentId.get(node.parent_id) ?? [];
    siblings.push(node);
    taxonomyChildrenByParentId.set(node.parent_id, siblings);
  }

  for (const siblings of taxonomyChildrenByParentId.values()) {
    siblings.sort((left, right) => {
      if (left.sort_order !== right.sort_order) {
        return left.sort_order - right.sort_order;
      }
      return compareText(left.label, right.label);
    });
  }

  for (const overlay of overlays) {
    overlayPreviewUrlById.set(overlay.id, getOverlayRecordPreviewUrl(overlay, assetById));
  }
  for (const membership of taxonomyNodeOverlays) {
    const overlay = overlayById.get(membership.overlay_id);
    if (!overlay) {
      continue;
    }
    const entries = decorationsByTaxonomyId.get(membership.taxonomy_node_id) ?? [];
    entries.push(overlay);
    decorationsByTaxonomyId.set(membership.taxonomy_node_id, entries);
  }
  for (const [taxonomyNodeId, overlays] of decorationsByTaxonomyId) {
    const sortOrderByOverlayId = new Map(
      taxonomyNodeOverlays
        .filter((membership) => membership.taxonomy_node_id === taxonomyNodeId)
        .map((membership) => [membership.overlay_id, membership.sort_order] as const),
    );
    overlays.sort(
      (left, right) =>
        (sortOrderByOverlayId.get(left.id) ?? 0) - (sortOrderByOverlayId.get(right.id) ?? 0) ||
        compareText(left.title, right.title),
    );
  }

  const orderEntriesByTileSetId = new Map<string, TileSetEntryRecord[]>();
  for (const entry of tileSetOrderEntries) {
    const entries = orderEntriesByTileSetId.get(entry.parent_tile_set_id) ?? [];
    entries.push(entry);
    orderEntriesByTileSetId.set(entry.parent_tile_set_id, entries);
  }

  /** Leaf tile ids reachable through ordered-brush child tile-set slots (matches catalog manifest resolution). */
  const resolvedTileIdsCache = new Map<string, string[]>();
  function resolveLeafTileIdsForTileSet(tileSetId: string, ancestry: Set<string>): string[] {
    const cached = resolvedTileIdsCache.get(tileSetId);
    if (cached) {
      return cached;
    }

    const sortedEntries = [...(orderEntriesByTileSetId.get(tileSetId) ?? [])].sort(
      (left, right) => left.sort_order - right.sort_order,
    );
    const directTileIds = sortedEntries
      .filter((entry) => entry.tile_id)
      .map((entry) => entry.tile_id as string);

    if (sortedEntries.length === 0) {
      resolvedTileIdsCache.set(tileSetId, directTileIds);
      return directTileIds;
    }

    const resolvedTileIds = sortedEntries.flatMap((entry) => {
      if (entry.tile_id) {
        return [entry.tile_id];
      }
      if (!entry.child_tile_set_id || ancestry.has(entry.child_tile_set_id)) {
        return [];
      }
      return resolveLeafTileIdsForTileSet(
        entry.child_tile_set_id,
        new Set([...ancestry, entry.child_tile_set_id]),
      );
    });
    const uniqueResolvedTileIds = [...new Set(resolvedTileIds)];
    resolvedTileIdsCache.set(tileSetId, uniqueResolvedTileIds);
    return uniqueResolvedTileIds;
  }

  for (const tileSet of tileSets) {
    const sortedEntries = [...(orderEntriesByTileSetId.get(tileSet.id) ?? [])].sort(
      (left, right) => left.sort_order - right.sort_order,
    );
    const resolvedEntries: TileSetOrderEntry[] = [];

    for (const entry of sortedEntries) {
      if (entry.tile_id) {
        const tile = tileById.get(entry.tile_id);
        if (!tile) {
          continue;
        }
        resolvedEntries.push({
          kind: "tile",
          id: tile.id,
          sortOrder: entry.sort_order,
          tile,
          recordId: entry.id,
        });
        continue;
      }

      if (!entry.child_tile_set_id) {
        continue;
      }

      const childTileSet = tileSetById.get(entry.child_tile_set_id);
      if (!childTileSet) {
        continue;
      }

      resolvedEntries.push({
        kind: "tile-set",
        id: childTileSet.id,
        sortOrder: entry.sort_order,
        tileSet: childTileSet,
        recordId: entry.id,
      });
    }

    const resolvedTiles = resolveLeafTileIdsForTileSet(tileSet.id, new Set([tileSet.id]))
      .map((id) => tileById.get(id))
      .filter((tile): tile is TileRecord => Boolean(tile));

    tilesByTileSetId.set(tileSet.id, resolvedTiles);
    tileSetOrderEntriesByTileSetId.set(tileSet.id, resolvedEntries);
    tileCountByTileSetId.set(tileSet.id, resolvedTiles.length);
  }

  const taxonomyTileSetMembershipsByParentId = new Map<string | null, TileSetTaxonomyNodeRecord[]>();
  for (const membership of tileSetTaxonomyNodes) {
    const entries = taxonomyTileSetMembershipsByParentId.get(membership.taxonomy_node_id) ?? [];
    entries.push(membership);
    taxonomyTileSetMembershipsByParentId.set(membership.taxonomy_node_id, entries);
  }

  const mixedEntryParentIds = new Set<string | null>([null]);
  for (const node of taxonomyNodes) {
    mixedEntryParentIds.add(node.parent_id);
  }
  for (const parentId of taxonomyTileSetMembershipsByParentId.keys()) {
    mixedEntryParentIds.add(parentId);
  }

  for (const parentId of mixedEntryParentIds) {
    const folderEntries: TaxonomyMixedEntry[] = (taxonomyChildrenByParentId.get(parentId) ?? []).map((node) => ({
      kind: "taxonomy-node",
      id: node.id,
      label: node.label,
      sortOrder: node.sort_order,
      node,
    }));
    const tileSetEntries: TaxonomyMixedEntry[] = [...(taxonomyTileSetMembershipsByParentId.get(parentId) ?? [])]
      .sort((left, right) => left.sort_order - right.sort_order)
      .flatMap((membership): TaxonomyMixedEntry[] => {
        const tileSet = tileSetById.get(membership.tile_set_id);
        if (!tileSet) {
          return [];
        }
        const directTileSets = directTileSetsByTaxonomyId.get(parentId) ?? [];
        directTileSets.push(tileSet);
        directTileSetsByTaxonomyId.set(parentId, directTileSets);
        return [
          {
            kind: "tile-set",
            id: tileSet.id,
            label: tileSet.title,
            sortOrder: membership.sort_order,
            tileSet,
          },
        ];
      });

    taxonomyMixedEntriesByParentId.set(
      parentId,
      [...folderEntries, ...tileSetEntries].sort((left, right) => {
        if (left.sortOrder !== right.sortOrder) {
          return left.sortOrder - right.sortOrder;
        }
        return compareText(left.label, right.label);
      }),
    );
  }

  for (const tileSet of tileSets) {
    const defaultThumbnailUrl = tileSet.default_thumbnail_tile_id
      ? (tilePreviewUrlById.get(tileSet.default_thumbnail_tile_id) ?? null)
      : null;
    const fallbackTile = tilesByTileSetId.get(tileSet.id)?.[0];
    tileSetPreviewUrlById.set(
      tileSet.id,
      defaultThumbnailUrl ?? (fallbackTile ? (tilePreviewUrlById.get(fallbackTile.id) ?? null) : null),
    );
  }

  const subtreeTileSetIdsByTaxonomyId = new Map<string, Set<string>>();
  function collectSubtreeTileSetIds(taxonomyNodeId: string): Set<string> {
    const cached = subtreeTileSetIdsByTaxonomyId.get(taxonomyNodeId);
    if (cached) {
      return cached;
    }

    const subtreeTileSetIds = new Set<string>();
    for (const tileSet of directTileSetsByTaxonomyId.get(taxonomyNodeId) ?? []) {
      subtreeTileSetIds.add(tileSet.id);
    }
    for (const child of taxonomyChildrenByParentId.get(taxonomyNodeId) ?? []) {
      for (const tileSetId of collectSubtreeTileSetIds(child.id)) {
        subtreeTileSetIds.add(tileSetId);
      }
    }

    subtreeTileSetIdsByTaxonomyId.set(taxonomyNodeId, subtreeTileSetIds);
    return subtreeTileSetIds;
  }

  function resolveTaxonomyPreviewUrl(taxonomyNodeId: string): string | null {
    if (taxonomyPreviewUrlById.has(taxonomyNodeId)) {
      return taxonomyPreviewUrlById.get(taxonomyNodeId) ?? null;
    }

    const directTileSet = directTileSetsByTaxonomyId.get(taxonomyNodeId)?.[0];
    if (directTileSet) {
      const previewUrl = tileSetPreviewUrlById.get(directTileSet.id) ?? null;
      if (previewUrl) {
        taxonomyPreviewUrlById.set(taxonomyNodeId, previewUrl);
        return previewUrl;
      }
    }

    for (const child of taxonomyChildrenByParentId.get(taxonomyNodeId) ?? []) {
      const previewUrl = resolveTaxonomyPreviewUrl(child.id);
      if (previewUrl) {
        taxonomyPreviewUrlById.set(taxonomyNodeId, previewUrl);
        return previewUrl;
      }
    }

    taxonomyPreviewUrlById.set(taxonomyNodeId, null);
    return null;
  }

  for (const node of data.taxonomyNodes) {
    resolveTaxonomyPreviewUrl(node.id);
  }

  return {
    assetById,
    overlayById,
    tileById,
    tileSetById,
    tilesByTileSetId,
    decorationsByTaxonomyId,
    tileSetOrderEntriesByTileSetId,
    tileCountByTileSetId,
    taxonomyChildrenByParentId,
    taxonomyMixedEntriesByParentId,
    overlayPreviewUrlById,
    tileSetPreviewUrlById,
    taxonomyPreviewUrlById,
  };
}

/** JSON body for POST `/api/admin/system-catalog/taxonomy` when updating an existing folder. */
export function buildTaxonomyNodeUpsertPayload(
  node: TaxonomyNodeRecord,
  overrides: { label?: string; isDraft?: boolean; defaultThumbnailTileId?: string | null } = {},
): {
  id: string;
  parentId: string | null;
  label: string;
  sortOrder: number;
  isOfficial: boolean;
  isDraft: boolean;
  defaultThumbnailTileId: string | null;
} {
  const rawLabel = overrides.label !== undefined ? overrides.label : node.label;
  return {
    id: node.id,
    parentId: node.parent_id,
    label: rawLabel.trim(),
    sortOrder: node.sort_order,
    isOfficial: node.is_official,
    isDraft: overrides.isDraft ?? node.is_draft,
    defaultThumbnailTileId:
      overrides.defaultThumbnailTileId !== undefined
        ? overrides.defaultThumbnailTileId
        : null,
  };
}

/** JSON body for POST `/api/admin/system-catalog/tile-set` when updating an existing tile set. */
export function buildTileSetUpsertPayload(
  tileSet: TileSetRecord,
  overrides: {
    title?: string;
    paintMode?: MultiTilePaintMode;
    defaultBrowseLabel?: string | null;
    defaultThumbnailTileId?: string | null;
  } = {},
): {
  tileSetId: string;
  title: string;
  description: string;
  visibility: string;
  enabled: boolean;
  sortOrder: number;
  paintMode: MultiTilePaintMode | undefined;
  defaultBrowseLabel: string | null;
  defaultThumbnailTileId: string | null;
} {
  const title = (overrides.title ?? tileSet.title).trim();
  const normalizedExistingBrowseLabel = tileSet.default_browse_label?.trim() || null;
  const normalizedExistingTitle = tileSet.title.trim();
  const defaultBrowseLabel =
    overrides.defaultBrowseLabel !== undefined
      ? overrides.defaultBrowseLabel?.trim() || null
      : normalizedExistingBrowseLabel === null || normalizedExistingBrowseLabel === normalizedExistingTitle
        ? title
        : normalizedExistingBrowseLabel;

  return {
    tileSetId: tileSet.id,
    title,
    description: tileSet.description ?? "",
    visibility: tileSet.visibility,
    enabled: tileSet.enabled,
    sortOrder: tileSet.sort_order,
    paintMode: overrides.paintMode ?? tileSet.paint_mode,
    defaultBrowseLabel,
    defaultThumbnailTileId:
      overrides.defaultThumbnailTileId !== undefined
        ? overrides.defaultThumbnailTileId
        : tileSet.default_thumbnail_tile_id,
  };
}

function compareText(left: string, right: string): number {
  return left.localeCompare(right, undefined, { sensitivity: "base" });
}

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids.filter(Boolean))];
}

export function getTaxonomyMixedEntryKey(entry: Pick<TaxonomyMixedEntry, "kind" | "id">): string {
  return `${entry.kind}:${entry.id}`;
}

function sortTileSets(tileSets: TileSetRecord[]): TileSetRecord[] {
  return [...tileSets].sort((left, right) => {
    if (left.sort_order !== right.sort_order) {
      return left.sort_order - right.sort_order;
    }
    return compareText(left.title, right.title);
  });
}

function sortCollections(data: TileCatalogAdminData): CollectionRecord[] {
  const orderIndex = new Map(data.settings.collectionOrder.map((id, index) => [id, index]));
  return [...data.collections].sort((left, right) => {
    const leftIndex = orderIndex.get(left.id);
    const rightIndex = orderIndex.get(right.id);
    if (leftIndex !== undefined || rightIndex !== undefined) {
      if (leftIndex === undefined) {
        return 1;
      }
      if (rightIndex === undefined) {
        return -1;
      }
      if (leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
      }
    }
    return compareText(left.title, right.title);
  });
}

export function sortTaxonomyNodes(
  data: TileCatalogAdminData,
  parentId: string | null,
): TaxonomyNodeRecord[] {
  return [...data.taxonomyNodes]
    .filter((node) => node.parent_id === parentId)
    .sort((left, right) => {
      if (left.sort_order !== right.sort_order) {
        return left.sort_order - right.sort_order;
      }
      return compareText(left.label, right.label);
    });
}

function sortMembershipTileSets(
  tileSets: TileSetRecord[],
  memberships: Array<CollectionTileSetRecord | TileSetTaxonomyNodeRecord>,
): TileSetRecord[] {
  const tileSetById = new Map(tileSets.map((tileSet) => [tileSet.id, tileSet]));
  return [...memberships]
    .sort((left, right) => left.sort_order - right.sort_order)
    .map((membership) => tileSetById.get(membership.tile_set_id))
    .filter((tileSet): tileSet is TileSetRecord => Boolean(tileSet));
}

export function getCollectionTileSets(
  data: TileCatalogAdminData,
  collectionId: string,
): TileSetRecord[] {
  return sortMembershipTileSets(
    data.tileSets,
    data.collectionTileSets.filter((membership) => membership.collection_id === collectionId),
  );
}

export function getTaxonomyTileSets(data: TileCatalogAdminData, taxonomyNodeId: string): TileSetRecord[] {
  return sortMembershipTileSets(
    data.tileSets,
    data.tileSetTaxonomyNodes.filter((membership) => membership.taxonomy_node_id === taxonomyNodeId),
  );
}

export function getTilesForTileSet(data: TileCatalogAdminData, tileSetId: string): TileRecord[] {
  const tileById = new Map(data.tiles.map((tile) => [tile.id, tile] as const));
  return data.tileSetOrderEntries
    .filter((entry) => entry.parent_tile_set_id === tileSetId && entry.tile_id !== null)
    .sort((left, right) => left.sort_order - right.sort_order)
    .flatMap((entry) => {
      const tile = entry.tile_id ? tileById.get(entry.tile_id) : null;
      return tile ? [tile] : [];
    });
}

export function getTileSetOrderEntries(
  data: TileCatalogAdminData,
  tileSetId: string,
): TileSetOrderEntry[] {
  const tileById = new Map(data.tiles.map((tile) => [tile.id, tile] as const));
  const tileSetById = new Map(data.tileSets.map((tileSet) => [tileSet.id, tileSet] as const));
  const explicitEntries = data.tileSetOrderEntries
    .filter((entry) => entry.parent_tile_set_id === tileSetId)
    .sort((left, right) => left.sort_order - right.sort_order)
    .flatMap((entry): TileSetOrderEntry[] => {
      if (entry.tile_id) {
        const tile = tileById.get(entry.tile_id);
        if (!tile) {
          return [];
        }
        return [
          {
            kind: "tile",
            id: tile.id,
            sortOrder: entry.sort_order,
            tile,
            recordId: entry.id,
          },
        ];
      }

      if (!entry.child_tile_set_id) {
        return [];
      }

      const childTileSet = tileSetById.get(entry.child_tile_set_id);
      if (!childTileSet) {
        return [];
      }

      return [
        {
          kind: "tile-set",
          id: childTileSet.id,
          sortOrder: entry.sort_order,
          tileSet: childTileSet,
          recordId: entry.id,
        },
      ];
    });

  return explicitEntries;
}

export function applyReplaceTileSetOrderEntries(
  data: TileCatalogAdminData,
  tileSetId: string,
  entries: Array<{ kind: "tile" | "tile-set"; id: string }>,
): TileCatalogAdminData {
  const nextEntries: TileSetEntryRecord[] = entries.map((entry, index) => ({
    id: crypto.randomUUID(),
    parent_tile_set_id: tileSetId,
    sort_order: index,
    tile_id: entry.kind === "tile" ? entry.id : null,
    child_tile_set_id: entry.kind === "tile-set" ? entry.id : null,
  }));

  return {
    ...data,
    tileSetOrderEntries: [
      ...data.tileSetOrderEntries.filter((entry) => entry.parent_tile_set_id !== tileSetId),
      ...nextEntries,
    ],
  };
}

export function buildAppendedTileSetOrderEntries(
  data: TileCatalogAdminData,
  tileSetId: string,
  appendedEntry: { kind: "tile" | "tile-set"; id: string },
): Array<{ kind: "tile" | "tile-set"; id: string }> {
  const existingEntries = getTileSetOrderEntries(data, tileSetId).map((entry) => ({
    kind: entry.kind,
    id: entry.id,
  }));

  if (existingEntries.some((entry) => entry.kind === appendedEntry.kind && entry.id === appendedEntry.id)) {
    return existingEntries;
  }

  return [...existingEntries, appendedEntry];
}

export function getTaxonomyMaxEntrySortOrder(
  data: TileCatalogAdminData,
  parentId: string | null,
): number {
  const childSortOrders = data.taxonomyNodes
    .filter((node) => node.parent_id === parentId)
    .map((node) => node.sort_order);
  const tileSetSortOrders =
    data.tileSetTaxonomyNodes
      .filter((membership) => membership.taxonomy_node_id === parentId)
      .map((membership) => membership.sort_order);

  return [...childSortOrders, ...tileSetSortOrders].reduce(
    (highest, sortOrder) => Math.max(highest, sortOrder),
    -1,
  );
}

export function getTaxonomyMixedEntries(
  data: TileCatalogAdminData,
  taxonomyNodeId: string | null,
): TaxonomyMixedEntry[] {
  const tileSetById = new Map(data.tileSets.map((tileSet) => [tileSet.id, tileSet]));
  const folderEntries: TaxonomyMixedEntry[] = sortTaxonomyNodes(data, taxonomyNodeId).map((node) => ({
    kind: "taxonomy-node" as const,
    id: node.id,
    label: node.label,
    sortOrder: node.sort_order,
    node,
  }));
  const tileSetEntries: TaxonomyMixedEntry[] = [];

  for (const membership of data.tileSetTaxonomyNodes.filter(
    (entry) => entry.taxonomy_node_id === taxonomyNodeId,
  )) {
    const tileSet = tileSetById.get(membership.tile_set_id);
    if (!tileSet) {
      continue;
    }
    tileSetEntries.push({
      kind: "tile-set",
      id: tileSet.id,
      label: tileSet.title,
      sortOrder: membership.sort_order,
      tileSet,
    });
  }

  const entries: TaxonomyMixedEntry[] = [...folderEntries, ...tileSetEntries];

  return entries.sort((left, right) => {
    if (left.sortOrder !== right.sortOrder) {
      return left.sortOrder - right.sortOrder;
    }
    return compareText(left.label, right.label);
  });
}

export function getOrphanTileSets(data: TileCatalogAdminData): TileSetRecord[] {
  const attachedTileSetIds = new Set<string>();
  for (const membership of data.collectionTileSets) {
    attachedTileSetIds.add(membership.tile_set_id);
  }
  for (const membership of data.tileSetTaxonomyNodes) {
    attachedTileSetIds.add(membership.tile_set_id);
  }
  for (const entry of data.tileSetOrderEntries) {
    if (entry.child_tile_set_id) {
      attachedTileSetIds.add(entry.child_tile_set_id);
    }
  }
  return sortTileSets(data.tileSets.filter((tileSet) => !attachedTileSetIds.has(tileSet.id)));
}

export function getDaysRemainingUntilPurge(
  purgeAfter: string,
  now = new Date(),
): number {
  const diffMs = new Date(purgeAfter).getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
}

export function getPendingDeletionTiles(
  data: Pick<TileCatalogAdminData, "tiles" | "assets">,
  now = new Date(),
): PendingDeletionTile[] {
  const assetById = new Map(data.assets.map((asset) => [asset.id, asset] as const));
  return data.tiles
    .filter(
      (tile) =>
        tile.namespace === "system" &&
        typeof tile.archived_at === "string" &&
        typeof tile.purge_after === "string",
    )
    .sort((left, right) => {
      const leftTime = left.purge_after ? new Date(left.purge_after).getTime() : Number.POSITIVE_INFINITY;
      const rightTime = right.purge_after ? new Date(right.purge_after).getTime() : Number.POSITIVE_INFINITY;
      return leftTime - rightTime || left.filename.localeCompare(right.filename);
    })
    .map((tile) => ({
      tile,
      asset: assetById.get(tile.asset_id) ?? null,
      daysRemaining: getDaysRemainingUntilPurge(tile.purge_after ?? now.toISOString(), now),
      purgeAfter: tile.purge_after ?? now.toISOString(),
    }));
}

export function canDeleteEmptyTileSet(data: TileCatalogAdminData, tileSetId: string): boolean {
  return (
    getSystemTileSetDeletionError({
      tileSetId,
      exists: data.tileSets.some((tileSet) => tileSet.id === tileSetId),
      defaultOceanTileSetId: data.settings.defaultOceanTileSetId,
      defaultSecondaryPaintTileSetId: data.settings.defaultSecondaryPaintTileSetId,
      directTileCount: data.tiles.filter((tile) => tile.tile_set_id === tileSetId).length,
      parentEntryCount: data.tileSetOrderEntries.filter((entry) => entry.parent_tile_set_id === tileSetId).length,
      childReferenceCount: data.tileSetOrderEntries.filter((entry) => entry.child_tile_set_id === tileSetId).length,
    }) === null
  );
}

export function shouldDeleteDetachedTileSet(data: TileCatalogAdminData, tileSetId: string): boolean {
  return false;
}

function getTileAssetUrl(data: TileCatalogAdminData, tileId: string | null | undefined): string | null {
  if (!tileId) {
    return null;
  }

  const tile = data.tiles.find((entry) => entry.id === tileId);
  if (!tile) {
    return null;
  }

  return getTileRecordPreviewUrl(tile);
}

function getTaxonomyNodeById(
  data: TileCatalogAdminData,
  taxonomyNodeId: string,
): TaxonomyNodeRecord | null {
  return data.taxonomyNodes.find((entry) => entry.id === taxonomyNodeId) ?? null;
}

export function getTaxonomyDescendantIds(
  data: TileCatalogAdminData,
  taxonomyNodeId: string,
): string[] {
  const childIds = data.taxonomyNodes
    .filter((node) => node.parent_id === taxonomyNodeId)
    .map((node) => node.id);
  return [taxonomyNodeId, ...childIds.flatMap((childId) => getTaxonomyDescendantIds(data, childId))];
}

export function getTaxonomySubtreeTileSets(
  data: TileCatalogAdminData,
  taxonomyNodeId: string,
): TileSetRecord[] {
  const seen = new Set<string>();
  const out: TileSetRecord[] = [];

  function visit(nodeId: string) {
    for (const tileSet of getTaxonomyTileSets(data, nodeId)) {
      if (!seen.has(tileSet.id)) {
        seen.add(tileSet.id);
        out.push(tileSet);
      }
    }
    for (const child of getSortedTaxonomyChildren(data, nodeId)) {
      visit(child.id);
    }
  }

  visit(taxonomyNodeId);
  return out;
}

export function getTaxonomySubtreeTiles(
  data: TileCatalogAdminData,
  taxonomyNodeId: string,
): TileRecord[] {
  return getTaxonomySubtreeTileSets(data, taxonomyNodeId).flatMap((tileSet) => getTilesForTileSet(data, tileSet.id));
}

export function isTileInTaxonomySubtree(
  data: TileCatalogAdminData,
  taxonomyNodeId: string,
  tileId: string | null | undefined,
): boolean {
  if (!tileId) {
    return false;
  }

  const tile = data.tiles.find((entry) => entry.id === tileId);
  if (!tile) {
    return false;
  }

  const descendantIds = new Set(getTaxonomyDescendantIds(data, taxonomyNodeId));
  const tileSetIds = new Set(
    data.tileSetOrderEntries
      .filter((entry) => entry.tile_id === tile.id)
      .map((entry) => entry.parent_tile_set_id),
  );
  return data.tileSetTaxonomyNodes.some(
    (membership) =>
      tileSetIds.has(membership.tile_set_id) &&
      membership.taxonomy_node_id !== null &&
      descendantIds.has(membership.taxonomy_node_id),
  );
}

export function getDirectTileSetIdForTile(
  data: Pick<TileCatalogAdminData, "tileSetOrderEntries">,
  tileId: string,
): string | null {
  return data.tileSetOrderEntries.find((entry) => entry.tile_id === tileId)?.parent_tile_set_id ?? null;
}

export function getTileSetPreviewUrl(
  data: TileCatalogAdminData,
  tileSetId: string,
): string | null {
  const tileSet = data.tileSets.find((entry) => entry.id === tileSetId);
  const thumbnailUrl = getTileAssetUrl(data, tileSet?.default_thumbnail_tile_id);
  if (thumbnailUrl) {
    return thumbnailUrl;
  }

  const fallbackTile = getTilesForTileSet(data, tileSetId)[0];
  return fallbackTile ? getTileRecordPreviewUrl(fallbackTile) : null;
}

export function getCollectionPreviewUrl(
  data: TileCatalogAdminData,
  collectionId: string,
): string | null {
  const tileSetId = getCollectionTileSets(data, collectionId)[0]?.id;
  return tileSetId ? getTileSetPreviewUrl(data, tileSetId) : null;
}

function getSortedTaxonomyChildren(data: TileCatalogAdminData, parentId: string): TaxonomyNodeRecord[] {
  return sortTaxonomyNodes(data, parentId);
}

export function getTaxonomyPreviewUrl(
  data: TileCatalogAdminData,
  taxonomyNodeId: string,
): string | null {
  const directTileSetId = getTaxonomyTileSets(data, taxonomyNodeId)[0]?.id;
  if (directTileSetId) {
    return getTileSetPreviewUrl(data, directTileSetId);
  }

  for (const child of getSortedTaxonomyChildren(data, taxonomyNodeId)) {
    const childPreviewUrl = getTaxonomyPreviewUrl(data, child.id);
    if (childPreviewUrl) {
      return childPreviewUrl;
    }
  }

  return null;
}

export function moveId(ids: string[], targetId: string, direction: -1 | 1): string[] {
  const currentIndex = ids.indexOf(targetId);
  const nextIndex = currentIndex + direction;
  if (currentIndex === -1 || nextIndex < 0 || nextIndex >= ids.length) {
    return ids;
  }
  const next = [...ids];
  [next[currentIndex], next[nextIndex]] = [next[nextIndex], next[currentIndex]];
  return next;
}

/** Move `targetId` to a specific insertion index within `ids` (no-op if missing or unchanged). */
export function moveIdToIndex(ids: string[], targetId: string, index: number): string[] {
  const currentIndex = ids.indexOf(targetId);
  if (currentIndex === -1) {
    return ids;
  }

  const rest = ids.filter((id) => id !== targetId);
  const clampedIndex = Math.max(0, Math.min(index, rest.length));
  const next = [...rest];
  next.splice(clampedIndex, 0, targetId);

  if (next.every((id, nextIndex) => id === ids[nextIndex])) {
    return ids;
  }

  return next;
}

/** Move `targetId` to the first or last position among `ids` (no-op if missing or already there). */
export function moveIdToEdge(ids: string[], targetId: string, edge: "start" | "end"): string[] {
  const currentIndex = ids.indexOf(targetId);
  if (currentIndex === -1) {
    return ids;
  }
  const boundary = edge === "start" ? 0 : ids.length - 1;
  if (currentIndex === boundary) {
    return ids;
  }
  const rest = ids.filter((id) => id !== targetId);
  return edge === "start" ? [targetId, ...rest] : [...rest, targetId];
}

function reorderCollectionOrder(data: TileCatalogAdminData, ids: string[]): TileCatalogAdminData {
  const nextIds = uniqueIds(ids);
  const remainder = data.collections
    .map((collection) => collection.id)
    .filter((id) => !nextIds.includes(id));
  return {
    ...data,
    settings: {
      ...data.settings,
      collectionOrder: [...nextIds, ...remainder],
    },
  };
}

function reorderTaxonomySiblings(
  data: TileCatalogAdminData,
  parentId: string | null,
  ids: string[],
): TileCatalogAdminData {
  const nextIds = uniqueIds(ids);
  let nextSortOrder = 0;
  const sortOrderById = new Map<string, number>();
  for (const id of nextIds) {
    sortOrderById.set(id, nextSortOrder);
    nextSortOrder += 1;
  }
  return {
    ...data,
    taxonomyNodes: data.taxonomyNodes.map((node) => {
      if (node.parent_id !== parentId) {
        return node;
      }
      const sortOrder = sortOrderById.get(node.id);
      return sortOrder === undefined ? node : { ...node, sort_order: sortOrder };
    }),
  };
}

function reorderCollectionTileSets(
  data: TileCatalogAdminData,
  collectionId: string,
  ids: string[],
): TileCatalogAdminData {
  const sortOrderById = new Map(uniqueIds(ids).map((id, index) => [id, index]));
  return {
    ...data,
    collectionTileSets: data.collectionTileSets.map((membership) => {
      if (membership.collection_id !== collectionId) {
        return membership;
      }
      const sortOrder = sortOrderById.get(membership.tile_set_id);
      return sortOrder === undefined ? membership : { ...membership, sort_order: sortOrder };
    }),
  };
}

function reorderTaxonomyTileSets(
  data: TileCatalogAdminData,
  taxonomyNodeId: string,
  ids: string[],
): TileCatalogAdminData {
  const sortOrderById = new Map(uniqueIds(ids).map((id, index) => [id, index]));
  return {
    ...data,
    tileSetTaxonomyNodes: data.tileSetTaxonomyNodes.map((membership) => {
      if (membership.taxonomy_node_id !== taxonomyNodeId) {
        return membership;
      }
      const sortOrder = sortOrderById.get(membership.tile_set_id);
      return sortOrder === undefined ? membership : { ...membership, sort_order: sortOrder };
    }),
  };
}

function reorderTiles(
  data: TileCatalogAdminData,
  tileSetId: string,
  ids: string[],
): TileCatalogAdminData {
  const sortOrderById = new Map(uniqueIds(ids).map((id, index) => [id, index]));
  return {
    ...data,
    tiles: data.tiles.map((tile) => {
      const sortOrder = sortOrderById.get(tile.id);
      return sortOrder === undefined ? tile : { ...tile, sort_order: sortOrder };
    }),
    tileSetOrderEntries: data.tileSetOrderEntries.map((entry) => {
      if (entry.parent_tile_set_id !== tileSetId || !entry.tile_id) {
        return entry;
      }
      const sortOrder = sortOrderById.get(entry.tile_id);
      return sortOrder === undefined ? entry : { ...entry, sort_order: sortOrder };
    }),
  };
}

function reorderTileSetOrderEntries(
  data: TileCatalogAdminData,
  tileSetId: string,
  entries: Array<{ kind: "tile" | "tile-set"; id: string }>,
): TileCatalogAdminData {
  const sortOrderByKey = new Map<string, number>(
    entries.map((entry, index) => [`${entry.kind}:${entry.id}`, index] as const),
  );
  return {
    ...data,
    tileSetOrderEntries: data.tileSetOrderEntries.map((entry) => {
      if (entry.parent_tile_set_id !== tileSetId) {
        return entry;
      }

      const key =
        entry.tile_id !== null
          ? `tile:${entry.tile_id}`
          : entry.child_tile_set_id !== null
            ? `tile-set:${entry.child_tile_set_id}`
            : null;
      if (!key) {
        return entry;
      }

      const sortOrder = sortOrderByKey.get(key);
      return sortOrder === undefined ? entry : { ...entry, sort_order: sortOrder };
    }),
  };
}

function reorderTaxonomyEntries(
  data: TileCatalogAdminData,
  taxonomyNodeId: string | null,
  entries: Array<Pick<TaxonomyMixedEntry, "kind" | "id">>,
): TileCatalogAdminData {
  const sortOrderByKey = new Map(
    entries.map((entry, index) => [getTaxonomyMixedEntryKey(entry), index] as const),
  );

  return {
    ...data,
    taxonomyNodes: data.taxonomyNodes.map((node) => {
      if (node.parent_id !== taxonomyNodeId) {
        return node;
      }
      const sortOrder = sortOrderByKey.get(getTaxonomyMixedEntryKey({ kind: "taxonomy-node", id: node.id }));
      return sortOrder === undefined ? node : { ...node, sort_order: sortOrder };
    }),
    tileSetTaxonomyNodes: data.tileSetTaxonomyNodes.map((membership) => {
      if (membership.taxonomy_node_id !== taxonomyNodeId) {
        return membership;
      }
      const sortOrder = sortOrderByKey.get(
        getTaxonomyMixedEntryKey({ kind: "tile-set", id: membership.tile_set_id }),
      );
      return sortOrder === undefined ? membership : { ...membership, sort_order: sortOrder };
    }),
  };
}

function reorderTaxonomyDecorations(
  data: TileCatalogAdminData,
  taxonomyNodeId: string | null,
  ids: string[],
): TileCatalogAdminData {
  const sortOrderById = new Map(uniqueIds(ids).map((id, index) => [id, index]));
  return {
    ...data,
    taxonomyNodeOverlays: data.taxonomyNodeOverlays.map((membership) => {
      if (membership.taxonomy_node_id !== taxonomyNodeId) {
        return membership;
      }
      const sortOrder = sortOrderById.get(membership.overlay_id);
      return sortOrder === undefined ? membership : { ...membership, sort_order: sortOrder };
    }),
  };
}

export function applyReorder(
  data: TileCatalogAdminData,
  input:
    | { kind: "collections"; ids: string[] }
    | { kind: "taxonomy"; parentId: string | null; ids: string[] }
    | {
        kind: "taxonomy-entries";
        parentId: string | null;
        entries: Array<Pick<TaxonomyMixedEntry, "kind" | "id">>;
      }
    | { kind: "collection-tile-sets"; parentId: string; ids: string[] }
    | { kind: "taxonomy-tile-sets"; parentId: string; ids: string[] }
    | { kind: "tiles"; parentId: string; ids: string[] }
    | {
        kind: "tile-set-order-entries";
        parentId: string;
        entries: Array<{ kind: "tile" | "tile-set"; id: string }>;
      }
    | { kind: "taxonomy-decorations"; parentId: string | null; ids: string[] },
): TileCatalogAdminData {
  if (input.kind === "collections") {
    return reorderCollectionOrder(data, input.ids);
  }
  if (input.kind === "taxonomy") {
    return reorderTaxonomySiblings(data, input.parentId, input.ids);
  }
  if (input.kind === "taxonomy-entries") {
    return reorderTaxonomyEntries(data, input.parentId, input.entries);
  }
  if (input.kind === "collection-tile-sets") {
    return reorderCollectionTileSets(data, input.parentId, input.ids);
  }
  if (input.kind === "taxonomy-tile-sets") {
    return reorderTaxonomyTileSets(data, input.parentId, input.ids);
  }
  if (input.kind === "tile-set-order-entries") {
    return reorderTileSetOrderEntries(data, input.parentId, input.entries);
  }
  if (input.kind === "taxonomy-decorations") {
    return reorderTaxonomyDecorations(data, input.parentId, input.ids);
  }
  return reorderTiles(data, input.parentId, input.ids);
}

export function canMoveTaxonomyNode(
  data: TileCatalogAdminData,
  taxonomyNodeId: string,
  targetParentId: string | null,
): boolean {
  if (taxonomyNodeId === targetParentId) {
    return false;
  }
  if (targetParentId === null) {
    return true;
  }
  return !getTaxonomyDescendantIds(data, taxonomyNodeId).includes(targetParentId);
}

export function applyMoveTaxonomyNode(
  data: TileCatalogAdminData,
  input: { id: string; parentId: string | null; sortOrder: number },
): TileCatalogAdminData {
  return {
    ...data,
    taxonomyNodes: data.taxonomyNodes.map((node) =>
      node.id === input.id
        ? {
            ...node,
            parent_id: input.parentId,
            sort_order: input.sortOrder,
          }
        : node,
    ),
  };
}

export function applyAttach(
  data: TileCatalogAdminData,
  input:
    | { kind: "collection"; parentId: string; tileSetId: string }
    | { kind: "taxonomy"; parentId: string | null; tileSetId: string }
    | { kind: "decoration"; parentId: string | null; overlayId: string },
): TileCatalogAdminData {
  if (input.kind === "collection") {
    const existing = data.collectionTileSets.find(
      (membership) =>
        membership.collection_id === input.parentId && membership.tile_set_id === input.tileSetId,
    );
    if (existing) {
      return data;
    }
    const nextSortOrder =
      data.collectionTileSets
        .filter((membership) => membership.collection_id === input.parentId)
        .reduce((highest, membership) => Math.max(highest, membership.sort_order), -1) + 1;
    return {
      ...data,
      collectionTileSets: [
        ...data.collectionTileSets,
        {
          collection_id: input.parentId,
          tile_set_id: input.tileSetId,
          sort_order: nextSortOrder,
        },
      ],
    };
  }

  if (input.kind === "decoration") {
    const existing = data.taxonomyNodeOverlays.find(
      (membership) =>
        membership.taxonomy_node_id === input.parentId && membership.overlay_id === input.overlayId,
    );
    if (existing) {
      return data;
    }
    const nextSortOrder =
      data.taxonomyNodeOverlays
        .filter((membership) => membership.taxonomy_node_id === input.parentId)
        .reduce((highest, membership) => Math.max(highest, membership.sort_order), -1) + 1;
    return {
      ...data,
      taxonomyNodeOverlays: [
        ...data.taxonomyNodeOverlays,
        {
          taxonomy_node_id: input.parentId,
          overlay_id: input.overlayId,
          sort_order: nextSortOrder,
        },
      ],
    };
  }

  const existing = data.tileSetTaxonomyNodes.find(
    (membership) =>
      membership.taxonomy_node_id === input.parentId && membership.tile_set_id === input.tileSetId,
  );
  if (existing) {
    return data;
  }
  const nextSortOrder =
    getTaxonomyMaxEntrySortOrder(data, input.parentId) + 1;
  return {
    ...data,
    tileSetTaxonomyNodes: [
      ...data.tileSetTaxonomyNodes,
      {
        tile_set_id: input.tileSetId,
        taxonomy_node_id: input.parentId,
        sort_order: nextSortOrder,
      },
    ],
  };
}

export function applyMoveTaxonomyTileSet(
  data: TileCatalogAdminData,
  input: { tileSetId: string; sourceParentId: string | null; targetParentId: string | null },
): TileCatalogAdminData {
  if (input.sourceParentId === input.targetParentId) {
    return data;
  }

  const withoutSource =
    input.sourceParentId === null
      ? data
      : applyDelete(data, {
          kind: "taxonomy-tile-set",
          parentId: input.sourceParentId,
          id: input.tileSetId,
        });

  return applyAttach(withoutSource, {
    kind: "taxonomy",
    parentId: input.targetParentId,
    tileSetId: input.tileSetId,
  });
}

export function applyMoveTileBetweenTileSets(
  data: TileCatalogAdminData,
  input: MoveSystemTileResult,
): TileCatalogAdminData {
  const sourceSortOrderByEntryId = new Map(
    (input.sourceEntrySortOrderUpdates ?? []).map((update) => [update.entryId, update.sortOrder] as const),
  );
  const sourceSortOrderByTileId = new Map(
    (input.sourceTileSortOrderUpdates ?? []).map((update) => [update.tileId, update.sortOrder] as const),
  );

  return {
    ...data,
    tileSets: data.tileSets.map((tileSet) => {
      if (tileSet.id === input.sourceTileSet.id) {
        return input.sourceTileSet;
      }
      if (tileSet.id === input.destinationTileSet.id) {
        return input.destinationTileSet;
      }
      return tileSet;
    }),
    tiles: data.tiles.map((tile) => {
      if (tile.id === input.tile.id) {
        return input.tile;
      }
      const nextSortOrder = sourceSortOrderByTileId.get(tile.id);
      return nextSortOrder === undefined ? tile : { ...tile, sort_order: nextSortOrder };
    }),
    tileSetOrderEntries: [
      ...data.tileSetOrderEntries
        .filter((entry) => !(entry.parent_tile_set_id === input.sourceTileSet.id && entry.tile_id === input.tile.id))
        .map((entry) => {
          const nextSortOrder = sourceSortOrderByEntryId.get(entry.id);
          return nextSortOrder === undefined ? entry : { ...entry, sort_order: nextSortOrder };
        }),
      ...(input.movedEntry ? [input.movedEntry] : []),
    ],
  };
}

export function applyArchiveTile(
  data: TileCatalogAdminData,
  input: {
    tileId: string;
    assetId: string;
    tileSetId: string;
    archivedAt: string;
    purgeAfter: string;
    nextTileSetThumbnailId: string | null;
  },
): TileCatalogAdminData {
  const remainingTileSetEntries = data.tileSetOrderEntries
    .filter((entry) => !(entry.parent_tile_set_id === input.tileSetId && entry.tile_id === input.tileId))
    .sort((left, right) => left.sort_order - right.sort_order);
  const nextSortOrderByEntryId = new Map(
    remainingTileSetEntries
      .filter((entry) => entry.parent_tile_set_id === input.tileSetId)
      .map((entry, index) => [entry.id, index] as const),
  );

  return {
    ...data,
    tiles: data.tiles.map((tile) =>
      tile.id === input.tileId
        ? {
            ...tile,
            tile_set_id: null,
            archived_at: input.archivedAt,
            purge_after: input.purgeAfter,
          }
        : tile,
    ),
    assets: data.assets.map((asset) =>
      asset.id === input.assetId
        ? {
            ...asset,
            archived_at: input.archivedAt,
            purge_after: input.purgeAfter,
          }
        : asset,
    ),
    tileSetOrderEntries: remainingTileSetEntries
      .map((entry) =>
        entry.parent_tile_set_id !== input.tileSetId
          ? entry
          : {
              ...entry,
              sort_order: nextSortOrderByEntryId.get(entry.id) ?? entry.sort_order,
            },
      ),
    tileSets: data.tileSets.map((tileSet) =>
      tileSet.id === input.tileSetId
        ? {
            ...tileSet,
            default_thumbnail_tile_id: input.nextTileSetThumbnailId,
          }
        : tileSet,
    ),
  };
}

export function applyDelete(
  data: TileCatalogAdminData,
  input:
    | { kind: "collection"; id: string }
    | { kind: "taxonomy"; id: string }
    | { kind: "collection-tile-set"; parentId: string; id: string }
    | { kind: "taxonomy-tile-set"; parentId: string | null; id: string }
    | { kind: "tile-set"; id: string }
    | { kind: "decoration"; id: string; parentId: string | null },
): TileCatalogAdminData {
  if (input.kind === "collection") {
    return {
      ...data,
      collections: data.collections.filter((collection) => collection.id !== input.id),
      collectionTileSets: data.collectionTileSets.filter(
        (membership) => membership.collection_id !== input.id,
      ),
      settings: {
        ...data.settings,
        featuredCollectionIds: data.settings.featuredCollectionIds.filter((id) => id !== input.id),
        collectionOrder: data.settings.collectionOrder.filter((id) => id !== input.id),
      },
    };
  }

  if (input.kind === "taxonomy") {
    const removedIds = new Set(getTaxonomyDescendantIds(data, input.id));
    return {
      ...data,
      taxonomyNodes: data.taxonomyNodes.filter((node) => !removedIds.has(node.id)),
      taxonomyNodeOverlays: data.taxonomyNodeOverlays.filter(
        (membership) =>
          membership.taxonomy_node_id === null || !removedIds.has(membership.taxonomy_node_id),
      ),
      tileSetTaxonomyNodes: data.tileSetTaxonomyNodes.filter(
        (membership) =>
          membership.taxonomy_node_id === null || !removedIds.has(membership.taxonomy_node_id),
      ),
    };
  }

  if (input.kind === "collection-tile-set") {
    return {
      ...data,
      collectionTileSets: data.collectionTileSets.filter(
        (membership) =>
          !(
            membership.collection_id === input.parentId && membership.tile_set_id === input.id
          ),
      ),
    };
  }

  if (input.kind === "tile-set") {
    return {
      ...data,
      tileSets: data.tileSets.filter((tileSet) => tileSet.id !== input.id),
      tiles: data.tiles.filter((tile) => tile.tile_set_id !== input.id),
      tileSetOrderEntries: data.tileSetOrderEntries.filter(
        (entry) => entry.parent_tile_set_id !== input.id && entry.child_tile_set_id !== input.id,
      ),
      tileSetTags: data.tileSetTags.filter((membership) => membership.tile_set_id !== input.id),
      collectionTileSets: data.collectionTileSets.filter((membership) => membership.tile_set_id !== input.id),
      tileSetTaxonomyNodes: data.tileSetTaxonomyNodes.filter((membership) => membership.tile_set_id !== input.id),
      settings: {
        ...data.settings,
        defaultOceanTileSetId:
          data.settings.defaultOceanTileSetId === input.id ? undefined : data.settings.defaultOceanTileSetId,
        defaultSecondaryPaintTileSetId:
          data.settings.defaultSecondaryPaintTileSetId === input.id
            ? undefined
            : data.settings.defaultSecondaryPaintTileSetId,
      },
    };
  }

  if (input.kind === "decoration") {
    return {
      ...data,
      taxonomyNodeOverlays: data.taxonomyNodeOverlays.filter(
        (membership) =>
          !(membership.taxonomy_node_id === input.parentId && membership.overlay_id === input.id),
      ),
    };
  }

  return {
    ...data,
    tileSetTaxonomyNodes: data.tileSetTaxonomyNodes.filter(
      (membership) =>
        !(membership.taxonomy_node_id === input.parentId && membership.tile_set_id === input.id),
    ),
  };
}

export function getDeleteImpact(
  data: TileCatalogAdminData,
  input:
    | { kind: "collection"; id: string }
    | { kind: "taxonomy"; id: string }
    | { kind: "collection-tile-set"; parentId: string; id: string }
    | { kind: "taxonomy-tile-set"; parentId: string | null; id: string }
    | { kind: "tile-set"; id: string },
): SystemCatalogDeleteImpact {
  const next = applyDelete(data, input);
  const orphanIdsBefore = new Set(getOrphanTileSets(data).map((tileSet) => tileSet.id));
  const orphanIdsAfter = new Set(getOrphanTileSets(next).map((tileSet) => tileSet.id));
  let orphanTileSetCount = 0;
  for (const id of orphanIdsAfter) {
    if (!orphanIdsBefore.has(id)) {
      orphanTileSetCount += 1;
    }
  }

  if (input.kind === "collection") {
    return {
      folderCount: 1,
      tileSetCount: getCollectionTileSets(data, input.id).length,
      orphanTileSetCount,
    };
  }

  if (input.kind === "taxonomy") {
    const descendantIds = getTaxonomyDescendantIds(data, input.id);
    const impactedTileSetIds = new Set(
      data.tileSetTaxonomyNodes
        .filter(
          (membership) =>
            membership.taxonomy_node_id !== null &&
            descendantIds.includes(membership.taxonomy_node_id),
        )
        .map((membership) => membership.tile_set_id),
    );
    return {
      folderCount: descendantIds.length,
      tileSetCount: impactedTileSetIds.size,
      orphanTileSetCount,
    };
  }

  if (input.kind === "tile-set") {
    return {
      folderCount: 0,
      tileSetCount: 1,
      orphanTileSetCount,
    };
  }

  return {
    folderCount: 0,
    tileSetCount: 1,
    orphanTileSetCount,
  };
}

export function getRootCollections(data: TileCatalogAdminData): CollectionRecord[] {
  return sortCollections(data);
}

export function getRootTaxonomyNodes(data: TileCatalogAdminData): TaxonomyNodeRecord[] {
  return sortTaxonomyNodes(data, null);
}

export function getTaxonomyChildNodes(
  data: TileCatalogAdminData,
  parentId: string,
): TaxonomyNodeRecord[] {
  return sortTaxonomyNodes(data, parentId);
}
